import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Pencil, Trash2, Building2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function AdminCompanies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = q ? { q } : {};
      const { data } = await api.get("/admin/companies", { params });
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); /* eslint-disable-next-line */ }, [q]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      description: c.description || "",
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/companies/${editing.company_id}`, {
        name: form.name.trim(),
        description: form.description.trim(),
      });
      toast.success("Empresa actualizada");
      setEditing(null);
      setTimeout(fetchCompanies, 250);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeCompany = async (id) => {
    try {
      await api.delete(`/admin/companies/${id}`);
      toast.success("Empresa eliminada");
      setTimeout(fetchCompanies, 250);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar empresa por nombre…"
            className="pl-10 h-11"
            data-testid="admin-companies-search"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Proyectos</TableHead>
                <TableHead>Creada</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Cargando…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Sin empresas</TableCell></TableRow>
              ) : (
                items.map((c) => (
                  <TableRow key={c.company_id} data-testid={`admin-company-row-${c.company_id}`}>
                    <TableCell className="max-w-[280px]">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          {c.description && (
                            <div className="text-xs text-muted-foreground truncate">{c.description}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.owner ? (
                        <div className="truncate">{c.owner.name}</div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Sin owner</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.projects_count ?? 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="icon"
                          title="Editar"
                          onClick={() => openEdit(c)}
                          data-testid={`admin-company-edit-${c.company_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost" size="icon"
                              className="text-destructive hover:text-destructive"
                              title="Eliminar"
                              data-testid={`admin-company-delete-${c.company_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar "{c.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                La empresa se eliminará. Los {c.projects_count || 0} proyectos asociados se desvincularán pero mantendrán el nombre. Acción irreversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => removeCompany(c.company_id)}
                                data-testid={`admin-company-confirm-delete-${c.company_id}`}
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-company-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar empresa</DialogTitle>
            <DialogDescription>
              Actualiza los datos de <span className="font-medium">{editing?.name}</span>.
              El cambio de nombre se propagará a sus proyectos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 h-11"
                data-testid="admin-company-name"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Descripción</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5"
                data-testid="admin-company-description"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button className="rounded-full" onClick={save} disabled={saving} data-testid="admin-company-save">
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
